import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Mic } from "lucide-react";

/**
 * Self-playing, looping demo: a scripted clinical conversation streams into a slim
 * transcript card while a Subjective summary + capture chips build in an overlapping
 * card — Notation's UI language, no user interaction.
 */

interface Line {
  speaker: "Clinician" | "Patient";
  text: string;
}

const TRANSCRIPT: Line[] = [
  { speaker: "Clinician", text: "Hi, I'm Christina. What brings you in today?" },
  { speaker: "Patient", text: "I've had right shoulder pain for about a month." },
  { speaker: "Patient", text: "It came on gradually and keeps getting worse." },
  { speaker: "Clinician", text: "Was there a specific incident?" },
  { speaker: "Patient", text: "Started after a weekend of painting the ceiling." },
  { speaker: "Patient", text: "It's a dull ache, but sometimes sharp." },
];

const SUBJECTIVE =
  "Patient presents with right shoulder pain, gradual onset over one month, progressively worsening. Began after overhead painting. Dull ache, intermittently sharp, aggravated by reaching.";
const SUBJECTIVE_WORDS = SUBJECTIVE.split(" ");

const CHIPS = [
  "Reason for Visit",
  "Primary Concern",
  "MOI",
  "Pain Location",
  "Pain Description",
  "PLOF",
  "Patient Goals",
];

/** Cumulative state per frame: transcript lines, subjective words, chips checked. */
const FRAMES: { t: number; s: number; c: number }[] = [
  { t: 1, s: 0, c: 0 },
  { t: 2, s: 0, c: 1 },
  { t: 3, s: 0, c: 1 },
  { t: 3, s: 6, c: 2 },
  { t: 4, s: 6, c: 2 },
  { t: 4, s: 12, c: 3 },
  { t: 5, s: 12, c: 4 },
  { t: 5, s: 18, c: 5 },
  { t: 6, s: 18, c: 5 },
  { t: 6, s: 24, c: 6 },
  { t: 6, s: 24, c: 7 },
];

const STEP_MS = 900;
const HOLD_MS = 2400;

export default function SubjectiveBuilderWidget() {
  const [frame, setFrame] = useState(0);
  const timeoutRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setFrame(FRAMES.length - 1);
      return;
    }

    const isLast = frame >= FRAMES.length - 1;
    timeoutRef.current = window.setTimeout(
      () => setFrame((f) => (f >= FRAMES.length - 1 ? 0 : f + 1)),
      isLast ? HOLD_MS : STEP_MS
    );

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [frame]);

  const { t, s, c } = FRAMES[frame];
  const lines = TRANSCRIPT.slice(0, t);
  const subjectiveText = SUBJECTIVE_WORDS.slice(0, s).join(" ");

  // Keep newest lines in view; older lines scroll off the top once full.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [t]);

  return (
    <div className="relative w-full h-full font-productSans">
      {/* Transcript — slim vertical card */}
      <div className="absolute left-0 top-[4%] bottom-[4%] w-[43%] rounded-[26px] bg-white shadow-[0_18px_50px_-16px_rgba(31,61,59,0.4)] overflow-hidden flex flex-col z-10">
        <div className="flex items-center justify-between px-3 py-2.5 bg-[#1F3D3B]">
          <span className="text-[11px] font-semibold text-white">Transcript</span>
          <span className="flex items-center gap-1.5 text-[9px] font-medium text-white/80">
            <Mic className="w-3 h-3" />
            <span className="flex items-end gap-[2px] h-3">
              {[0, 1, 2, 3].map((i) => (
                <motion.span
                  key={i}
                  className="w-[3px] rounded-full bg-[#ef4444]"
                  animate={{ height: ["30%", "100%", "45%"] }}
                  transition={{
                    duration: 0.7,
                    repeat: Infinity,
                    delay: i * 0.12,
                    ease: "easeInOut",
                  }}
                  style={{ height: "30%" }}
                />
              ))}
            </span>
            Transcribing
          </span>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 min-h-0 px-3 py-3 flex flex-col justify-start gap-2.5 overflow-hidden"
        >
          <AnimatePresence initial={false}>
            {lines.map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="shrink-0"
              >
                <p
                  className={`text-[9px] font-semibold mb-0.5 ${
                    line.speaker === "Clinician" ? "text-[#439CCB]" : "text-[#495B25]"
                  }`}
                >
                  {line.speaker}
                </p>
                <p className="text-[11px] leading-snug text-gray-700">{line.text}</p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Summary — overlaps the transcript's top-right */}
      <div className="absolute right-0 top-[15%] w-[66%] rounded-[26px] bg-white shadow-[0_24px_64px_-18px_rgba(31,61,59,0.45)] overflow-hidden z-20">
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] font-semibold text-[#1F3D3B] mb-2">Subjective</p>
          <p className="text-[12px] leading-relaxed text-gray-700 min-h-[4.5em]">
            {subjectiveText}
            {s > 0 && s < SUBJECTIVE_WORDS.length && (
              <motion.span
                className="inline-block w-[6px] h-[12px] ml-[1px] align-middle bg-[#439CCB]"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.7, repeat: Infinity, repeatType: "reverse" }}
              />
            )}
          </p>
        </div>

        <div className="px-4 pb-4 pt-1 flex flex-wrap gap-1.5">
          {CHIPS.map((chip, i) => {
            const checked = i < c;
            return (
              <motion.span
                key={chip}
                animate={{ scale: checked ? 1 : 0.98 }}
                transition={{ duration: 0.25 }}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors duration-300 ${
                  checked
                    ? "bg-[#486633] text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                <Check className={`w-3 h-3 ${checked ? "opacity-100" : "opacity-30"}`} />
                {chip}
              </motion.span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
